/**
 * This module exports pure functions for extracting the content of PQ data value cells.
 *
 * GraphQL PQ results were originally just simple values such as strings or numbers. When
 * we added Reports support to PQ's, they had to be able to return a url along with the value.
 *
 * These utilities are intended to make getting at both the underlying value and any url
 * easier and less error-prone by wrapping any dereference needed.
 */
import { has, isNumber, isObject, isString } from 'lodash';
import { Column } from 'common/graphqlTypes';
import { formatTimestamp } from 'common/util/time';
import { addCommas } from 'common/util/number';
import { ColumnFormat } from 'components/search/PowerQueriesPage/types';
import Link from 'components/common/Link/Link';

// PQ cells can have any number of value types
export type PQBaseDataValue = string | number | undefined | null | boolean;

// If a PQ cell is linked, there's an added level to dereference
export type PQLinkedValue = {
  url: string | null;
  value: PQBaseDataValue;
};

type PQValueFormattingOptions = {
  skipCommas?: boolean;
  timezone: string | null;
};

export const isPQLinkedValue = (pqLinkedValueCandidate: unknown): pqLinkedValueCandidate is PQLinkedValue => {
  if (!(pqLinkedValueCandidate && isObject(pqLinkedValueCandidate))) return false;

  const hasUrl = has(pqLinkedValueCandidate, 'url');
  const hasValue = has(pqLinkedValueCandidate, 'value');

  return hasUrl && hasValue;
};

// PQ cells can have any number of value types
export type PQDataValue = PQBaseDataValue | PQLinkedValue;

export type PQRowValue = Record<string, PQDataValue>;

/**
 * Return the url for this cell (if one was provided)
 *
 * @param value An undetermined cell value, possibly linked
 */
export const getPQUrl = (value: PQDataValue): string | null =>
  value && (value as unknown as PQLinkedValue).url !== undefined ? (value as PQLinkedValue).url : null;

/**
 * Return whatever the underlying value is, whether linked or not
 *
 * @param value An undetermined cell value, possibly linked
 */
export const getPQValue = (value: PQDataValue): PQBaseDataValue =>
  value && (value as unknown as PQLinkedValue).url !== undefined
    ? (value as PQLinkedValue).value
    : (value as PQBaseDataValue);

/**
 * Return the raw numeric value for the cell, whether linked or not.
 * Zero is returned as a soft failure if the value isn't actually a number.
 *
 * @param value An undetermined cell value, possibly linked
 */
export const getPQNumericValue = (value: PQDataValue): number => {
  const underlyingValue = getPQValue(value);
  return isNumber(underlyingValue) ? underlyingValue : 0;
};

/**
 * Return the raw string value for the cell, whether linked or not
 *
 * @param value An undetermined cell value, possibly linked
 */
export const getPQTextValue = (value: PQDataValue): string => String(getPQValue(value));

/**
 * Return the formatted string value for the cell, whether linked or not
 *
 * @param value  An undetermined cell value, possibly linked
 * @param header The column header for this cell
 */
export const getPQDisplayText = (
  value: PQDataValue,
  header: Column,
  formattingOptions?: PQValueFormattingOptions,
): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const currentValue = getPQValue(value);
  if (isString(currentValue)) {
    return currentValue;
  }

  if (isNumber(currentValue)) {
    const numberValue = currentValue;
    if (header.format === ColumnFormat.TIMESTAMP) {
      return formatTimestamp(numberValue, formattingOptions?.timezone ?? null);
    }
    const decimalPlaces = header.decimalPlaces != null ? header.decimalPlaces : 5;
    const number = numberValue.toFixed(decimalPlaces);
    return formattingOptions?.skipCommas ? number : addCommas(Number(number), undefined, decimalPlaces);
  }

  return String(currentValue);
};

export const DEFAULT_CELL_DISPLAY_VALUE = '—';

// Get the pure string value for the cell for display / filtering
// Potentially wrap the display value with a Link
export const getDisplayValue = (value: PQDataValue, header: Column, timezone: string | null): string | JSX.Element => {
  const displayValue = getPQDisplayText(value, header, { timezone });

  if (displayValue === 'null' || !displayValue) {
    return <span className="PowerQueries__NullValue">{DEFAULT_CELL_DISPLAY_VALUE}</span>;
  }

  if ((value as unknown as PQLinkedValue)?.url != null) {
    const cellValue = value as PQLinkedValue;
    return <Link href={cellValue.url || ''}>{displayValue}</Link>;
  }

  return displayValue;
};

/* A list of values cannot be rendered as a pie chart unless at least one of them is positive */
export const canBeRenderedAsPieChart = (values: number[]): boolean => values.some((value) => value > 0);
